import {
  getConversationRef,
  getMessagesRef,
  getOrCreateConversation,
  offersRef,
  serverTimestamp,
} from './collections';
import { createNotification } from './notifications';

const OFFER_EXPIRY_MS = 48 * 60 * 60 * 1000;

export async function createOffer({
  productId,
  productName,
  productImage,
  sellerId,
  buyerId,
  offerAmount,
  originalPrice,
  message,
  paymentMethod,
  deliveryPreference,
  meetingTime,
  offerType = 'money',
  barterProductName = '',
  barterProductImage = '',
  barterProductId = '',
  barterDescription = '',
  paymentToken = 'standard',
}) {
  const expiresAt = new Date(Date.now() + OFFER_EXPIRY_MS);
  const amt = Number(offerAmount) || 0;
  const isBarter = offerType === 'barter';

  // 1. Create the offer document
  const offerRef = await offersRef().add({
    productId,
    productName: productName || '',
    productImage: productImage || '',
    buyerId,
    sellerId,
    offerAmount: isBarter ? 0 : amt,
    originalPrice: Number(originalPrice) || 0,
    paymentToken,
    offerType: isBarter ? 'barter' : 'money',
    barterProductName: isBarter ? barterProductName : '',
    barterProductImage: isBarter ? barterProductImage : '',
    barterProductId: isBarter ? barterProductId : '',
    barterDescription: isBarter ? barterDescription : '',
    status: 'pending',
    counterAmount: null,
    message: message || '',
    paymentMethod: paymentMethod || '',
    deliveryPreference: deliveryPreference || '',
    meetingTime: meetingTime || null,
    negotiationHistory: [
      {
        amount: isBarter ? 0 : amt,
        by: buyerId,
        timestamp: new Date(),
        message: message || '',
        offerType: isBarter ? 'barter' : 'money',
      },
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: expiresAt,
  });

  const offerId = offerRef.id;

  // 2. Get or create a conversation and send the offer as a chat message
  try {
    const conversationId = await getOrCreateConversation(
      buyerId,
      sellerId,
      productId,
      productName,
      productImage,
      Number(originalPrice) || 0
    );

    if (conversationId) {
      const offerText = isBarter
        ? `Offered to trade for "${barterProductName || 'an item'}"${barterDescription ? ': ' + barterDescription : ''}`
        : `Made an offer of €${amt.toLocaleString('en-US')}`;
      await getMessagesRef(conversationId).add({
        senderId: buyerId,
        text: message ? `${offerText}\n"${message}"` : offerText,
        type: 'offer',
        offerAmount: isBarter ? 0 : amt,
        offerType: isBarter ? 'barter' : 'money',
        paymentToken,
        barterProductName: isBarter ? barterProductName : '',
        barterProductImage: isBarter ? barterProductImage : '',
        originalPrice: Number(originalPrice) || 0,
        offerId,
        timestamp: serverTimestamp(),
        read: false,
      });
      await getConversationRef(conversationId).update({
        lastMessage: offerText,
        lastMessageSender: buyerId,
        lastMessageType: 'offer',
        lastMessageAt: serverTimestamp(),
      });
    }
  } catch (err) {
    console.error('createOffer: failed to send chat message', err);
    // Don't fail the offer if chat message fails
  }

  // 3. Send notification to seller
  const notifText = isBarter
    ? `Someone offered to trade "${barterProductName || 'an item'}" for "${productName || 'your ad'}"`
    : `Someone offered €${amt.toLocaleString('en-US')} for "${productName || 'your ad'}"`;
  createNotification(
    sellerId,
    'offer',
    'New offer received',
    notifText,
    { productId, offerId }
  );

  return offerId;
}

export function updateOfferStatus(offerId, status, counterAmount) {
  const doc = offersRef().doc(offerId);
  const updates = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (counterAmount != null) updates.counterAmount = Number(counterAmount);
  return doc.update(updates);
}

export async function createBuyRequest({
  productName,
  category,
  desiredPrice,
  description,
  paymentMethod,
  deliveryPreference,
  userId,
}) {
  const expiresAt = new Date(Date.now() + OFFER_EXPIRY_MS);

  // Create a buy request document in the offers collection
  const offerRef = await offersRef().add({
    isBuyRequest: true,
    productName: productName || '',
    category: category || '',
    desiredPrice: Number(desiredPrice) || 0,
    description: description || '',
    userId,
    paymentMethod: paymentMethod || '',
    deliveryPreference: deliveryPreference || '',
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: expiresAt,
  });

  return offerRef.id;
}
